import { PeerCallManager, getPeerConnection } from './peer.js'
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Monitor,
  MessageSquare,
  PhoneOff,
  X,
  Copy,
  Send,
  Check,
  Settings,
  Volume2,
  VolumeX,
  ChevronUp,
} from 'lucide'
import { enableNoiseSuppression, disableNoiseSuppression, updateNoiseSuppressionSource } from './noise-suppression.js'
import {
  startCameraPipeline,
  loadFilters,
  saveFilters,
  filtersToCss,
  detectPipelineSupport,
  DEFAULT_FILTERS,
  type CameraFilters,
  type CameraPipeline,
} from './camera-filters.js'
import { startGainPipeline, loadGain, saveGain, type GainPipeline } from './mic-gain.js'
import './style.css'

// ─── Icon rendering (Lucide, tree-shaken) ───

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const iconMap: Record<string, any> = {
  mic: Mic,
  'mic-off': MicOff,
  video: Video,
  'video-off': VideoOff,
  monitor: Monitor,
  'message-square': MessageSquare,
  'phone-off': PhoneOff,
  x: X,
  copy: Copy,
  send: Send,
  check: Check,
  settings: Settings,
  'volume-2': Volume2,
  'volume-x': VolumeX,
  'chevron-up': ChevronUp,
}

function renderIcon(name: string, size = 24): string {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  const children: any = iconMap[name]
  if (!children) return ''
  const svgContent = children
    .map((child: any) => {
      const tag = child[0]
      const attrs = child[1]
      const attrsStr = Object.entries(attrs)
        .map(([k, v]: [string, any]) => `${k}="${v}"`)
        .join(' ')
      return `<${tag} ${attrsStr}/>`
    })
    .join('')
  /* eslint-enable @typescript-eslint/no-explicit-any */
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${svgContent}</svg>`
}

function initIcons() {
  document.querySelectorAll('[data-lucide]').forEach((el) => {
    const name = el.getAttribute('data-lucide') || ''
    if (name && iconMap[name]) {
      el.outerHTML = renderIcon(name)
    }
  })
}

function replaceIcon(btn: HTMLButtonElement, name: string) {
  btn.querySelectorAll('svg').forEach((s) => s.remove())
  btn.insertAdjacentHTML('beforeend', renderIcon(name))
}

// ─── DOM refs ───

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T

const lobby = $<HTMLDivElement>('lobby')
const callView = $<HTMLDivElement>('call')
const connecting = $<HTMLDivElement>('connecting')
const setupView = $<HTMLDivElement>('setup')
const setupVideo = $<HTMLVideoElement>('setup-video')
const setupNoCamera = $<HTMLDivElement>('setup-no-camera')
const setupMicLevel = $<HTMLDivElement>('setup-mic-level')
const setupTitle = $<HTMLHeadingElement>('setup-title')
const setupHint = $<HTMLParagraphElement>('setup-hint')
const setupName = $<HTMLInputElement>('setup-name')
const setupSelectCamera = $<HTMLSelectElement>('setup-select-camera')
const setupSelectMic = $<HTMLSelectElement>('setup-select-mic')
const setupSelectSpeaker = $<HTMLSelectElement>('setup-select-speaker')
const setupSpeakerField = $<HTMLLabelElement>('setup-speaker-field')
const setupToggleNoise = $<HTMLInputElement>('setup-toggle-noise')
const setupFilterBrightness = $<HTMLInputElement>('setup-filter-brightness')
const setupFilterContrast = $<HTMLInputElement>('setup-filter-contrast')
const setupFilterSaturation = $<HTMLInputElement>('setup-filter-saturation')
const setupFilterBlur = $<HTMLInputElement>('setup-filter-blur')
const setupValBrightness = $<HTMLSpanElement>('setup-val-brightness')
const setupValContrast = $<HTMLSpanElement>('setup-val-contrast')
const setupValSaturation = $<HTMLSpanElement>('setup-val-saturation')
const setupValBlur = $<HTMLSpanElement>('setup-val-blur')
const setupBtnReset = $<HTMLButtonElement>('setup-btn-reset-filters')
const btnSetupCancel = $<HTMLButtonElement>('btn-setup-cancel')
const btnSetupJoin = $<HTMLButtonElement>('btn-setup-join')
const connectingDetail = $<HTMLParagraphElement>('connecting-detail')
const videoGrid = $<HTMLDivElement>('video-grid')
const chatPanel = $<HTMLDivElement>('chat-panel')
const chatMessages = $<HTMLDivElement>('chat-messages')
const chatInput = $<HTMLInputElement>('input-chat')
const roomCode = $<HTMLSpanElement>('room-code')
const btnCreate = $<HTMLButtonElement>('btn-create')
const btnMic = $<HTMLButtonElement>('btn-mic')
const btnCamera = $<HTMLButtonElement>('btn-camera')
const btnScreen = $<HTMLButtonElement>('btn-screen')
const btnNoise = $<HTMLButtonElement>('btn-noise')
const btnChat = $<HTMLButtonElement>('btn-chat')
const btnLeave = $<HTMLButtonElement>('btn-leave')
const btnCloseChat = $<HTMLButtonElement>('btn-close-chat')
const btnCopy = $<HTMLButtonElement>('btn-copy')
const btnSettings = $<HTMLButtonElement>('btn-settings')
const btnCloseSettings = $<HTMLButtonElement>('btn-close-settings')
const settingsPanel = $<HTMLDivElement>('settings-panel')
const btnCameraMenu = $<HTMLButtonElement>('btn-camera-menu')
const btnCloseCamera = $<HTMLButtonElement>('btn-close-camera')
const cameraPanel = $<HTMLDivElement>('camera-panel')
const btnResetFilters = $<HTMLButtonElement>('btn-reset-filters')
const toggleMirror = $<HTMLInputElement>('toggle-mirror')
const setupToggleMirror = $<HTMLInputElement>('setup-toggle-mirror')
const filterGain = $<HTMLInputElement>('filter-gain')
const valGain = $<HTMLSpanElement>('val-gain')
const setupFilterGain = $<HTMLInputElement>('setup-filter-gain')
const setupValGain = $<HTMLSpanElement>('setup-val-gain')
const filterBrightness = $<HTMLInputElement>('filter-brightness')
const filterContrast = $<HTMLInputElement>('filter-contrast')
const filterSaturation = $<HTMLInputElement>('filter-saturation')
const filterBlur = $<HTMLInputElement>('filter-blur')
const valBrightness = $<HTMLSpanElement>('val-brightness')
const valContrast = $<HTMLSpanElement>('val-contrast')
const valSaturation = $<HTMLSpanElement>('val-saturation')
const valBlur = $<HTMLSpanElement>('val-blur')
const selectAudioInput = $<HTMLSelectElement>('select-audio-input')
const selectAudioOutput = $<HTMLSelectElement>('select-audio-output')
const selectVideoInput = $<HTMLSelectElement>('select-video-input')
const formJoin = $<HTMLFormElement>('form-join')
const inputCode = $<HTMLInputElement>('input-code')
const inputName = $<HTMLInputElement>('input-name')
const inputDisplayName = $<HTMLInputElement>('input-display-name')
const formChat = $<HTMLFormElement>('form-chat')

// ─── State ───

let manager: PeerCallManager
let localStream: MediaStream | null = null
let micOn = true
let cameraOn = true
let screenSharing = false
let noiseSuppression = false
let chatOpen = false
let mediaReady = false
let cameraPipeline: CameraPipeline | null = null
let cameraFilters: CameraFilters = loadFilters()
// Mirror the local preview only (what the user sees of themselves); the
// outgoing stream is never mirrored so remote peers see photo orientation.
// Default true — this is what most video-call users are accustomed to.
let cameraMirror: boolean = (() => {
  const saved = localStorage.getItem('peercall-mirror')
  return saved === null ? true : saved === 'true'
})()
let micGain: number = loadGain()
let gainPipeline: GainPipeline | null = null
// Last "source" audio track before gain — used to rebuild the gain stage
// when the slider changes without going back to getUserMedia / NS.
let lastMicSourceTrack: MediaStreamTrack | null = null
// WebGL is available in virtually every browser, including iPad Safari,
// so the pipeline works everywhere. `pipelineSupported` only flips false
// in the rare case WebGL context creation fails; in that case we fall
// back to CSS filters on the local <video> (local-only, not retransmitted).
const pipelineSupported = detectPipelineSupport()

const videoTiles = new Map<
  string,
  { container: HTMLDivElement; video: HTMLVideoElement; nameTag: HTMLSpanElement; mutedInd: HTMLDivElement }
>()

// ─── Feature detection ───

const sinkSupported = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype

// ─── Init ───

function init() {
  initIcons()

  // Restore saved name from localStorage
  const savedName = localStorage.getItem('peercall-name')
  if (savedName) inputName.value = savedName

  const hash = window.location.hash.slice(1).trim()
  const params = new URLSearchParams(window.location.search)
  const room = params.get('room') || hash

  if (room && room.length >= 4) {
    inputCode.value = room
    joinRoom(room, getUserName())
    return
  }

  btnCreate.addEventListener('click', () => createRoom(getUserName()))
  formJoin.addEventListener('submit', (e) => {
    e.preventDefault()
    const code = inputCode.value.trim().toLowerCase()
    if (code) joinRoom(code, getUserName())
  })
}

function getUserName(): string {
  const name = inputName.value.trim()
  if (name) localStorage.setItem('peercall-name', name)
  return name
}

// ─── Create / Join: route via setup screen ───

type PendingSetup = { mode: 'create' } | { mode: 'join'; roomCode: string }
let pendingSetup: PendingSetup | null = null

async function createRoom(name: string) {
  pendingSetup = { mode: 'create' }
  await startSetup(name)
}

async function joinRoom(code: string, name?: string) {
  pendingSetup = { mode: 'join', roomCode: code }
  await startSetup(name || getUserName())
}

async function startSetup(name: string) {
  showView('setup')
  manager = new PeerCallManager(name || undefined)
  setupManagerEvents(manager)

  // Fill UI from saved state
  setupName.value = manager.myName
  syncSetupFilterInputs()
  setupToggleNoise.checked = false
  if (pendingSetup?.mode === 'join') {
    setupTitle.textContent = 'Preparados para unirte'
    setupHint.textContent = `Sala: ${pendingSetup.roomCode}`
  } else {
    setupTitle.textContent = 'Preparados para empezar'
    setupHint.textContent = 'Comprueba tu cámara y audio antes de crear la sala'
  }

  await acquireMediaForSetup()
  await populateSetupDevices()
  applyMirrorSetting()
  attachSetupHandlers()
}

async function acquireMediaForSetup() {
  const prefs = getDevicePrefs()
  const deviceIds = { mic: prefs.mic, camera: prefs.camera }
  try {
    localStream = await manager.startMedia(true, true, deviceIds)
    mediaReady = true
    micOn = true
    cameraOn = true
    if (pipelineSupported) {
      await applyCameraPipeline()
    } else {
      applyCssFiltersToLocalPreview()
    }
    setupVideo.srcObject = localStream
    setupNoCamera.classList.add('hidden')
    const rawAudio = localStream.getAudioTracks()[0]
    if (rawAudio) await publishMic(rawAudio)
    startMicMeter()
  } catch {
    try {
      localStream = await manager.startMedia(false, true, deviceIds)
      mediaReady = true
      micOn = true
      cameraOn = false
      setupVideo.srcObject = null
      setupNoCamera.classList.remove('hidden')
      const rawAudio = localStream.getAudioTracks()[0]
      if (rawAudio) await publishMic(rawAudio)
      startMicMeter()
    } catch {
      mediaReady = false
      micOn = false
      cameraOn = false
      localStream = null
      setupVideo.srcObject = null
      setupNoCamera.classList.remove('hidden')
    }
  }
}

async function populateSetupDevices() {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const saved = getDevicePrefs()
    const currentMic = localStream?.getAudioTracks()[0]?.getSettings().deviceId
    const currentCamera = currentCameraDeviceId()

    fillSelect(
      setupSelectCamera,
      devices.filter((d) => d.kind === 'videoinput'),
      'Cámara',
      saved.camera || currentCamera,
    )
    fillSelect(
      setupSelectMic,
      devices.filter((d) => d.kind === 'audioinput'),
      'Micrófono',
      saved.mic || currentMic,
    )
    const outputs = devices.filter((d) => d.kind === 'audiooutput')
    if (!sinkSupported || outputs.length === 0) {
      setupSpeakerField.classList.add('hidden')
    } else {
      setupSpeakerField.classList.remove('hidden')
      fillSelect(setupSelectSpeaker, outputs, 'Altavoz', saved.speaker)
    }
  } catch (err) {
    console.error('[PeerCall] enumerateDevices failed:', err)
  }
}

function fillSelect(sel: HTMLSelectElement, devices: MediaDeviceInfo[], genericLabel: string, preferred?: string) {
  sel.innerHTML = ''
  if (devices.length === 0) {
    const opt = document.createElement('option')
    opt.value = ''
    opt.textContent = `Sin ${genericLabel.toLowerCase()}`
    opt.disabled = true
    sel.appendChild(opt)
    return
  }
  devices.forEach((d, i) => {
    const opt = document.createElement('option')
    opt.value = d.deviceId
    opt.textContent = d.label || `${genericLabel} ${i + 1}`
    sel.appendChild(opt)
  })
  if (preferred && devices.some((d) => d.deviceId === preferred)) {
    sel.value = preferred
  }
}

function syncSetupFilterInputs() {
  setupFilterBrightness.value = String(cameraFilters.brightness)
  setupFilterContrast.value = String(cameraFilters.contrast)
  setupFilterSaturation.value = String(cameraFilters.saturation)
  setupFilterBlur.value = String(cameraFilters.blur)
  setupValBrightness.textContent = `${Math.round(cameraFilters.brightness * 100)}%`
  setupValContrast.textContent = `${Math.round(cameraFilters.contrast * 100)}%`
  setupValSaturation.textContent = `${Math.round(cameraFilters.saturation * 100)}%`
  setupValBlur.textContent = `${cameraFilters.blur}px`
}

let setupHandlersAttached = false
function attachSetupHandlers() {
  if (setupHandlersAttached) return
  setupHandlersAttached = true

  setupName.addEventListener('change', () => {
    const v = setupName.value.trim()
    if (v) {
      localStorage.setItem('peercall-name', v)
      manager.rename(v)
    }
  })

  setupSelectCamera.addEventListener('change', async () => {
    const id = setupSelectCamera.value
    if (id) await switchCamera(id)
  })

  setupSelectMic.addEventListener('change', async () => {
    const id = setupSelectMic.value
    if (id) await switchMic(id)
  })

  setupSelectSpeaker.addEventListener('change', async () => {
    const id = setupSelectSpeaker.value
    if (id) await switchSpeaker(id)
  })

  setupToggleNoise.addEventListener('change', async () => {
    await setNoiseSuppressionEnabled(setupToggleNoise.checked)
  })

  setupToggleMirror.addEventListener('change', () => {
    setCameraMirror(setupToggleMirror.checked)
  })

  setupFilterGain.value = String(micGain)
  setupValGain.textContent = `${Math.round(micGain * 100)}%`
  setupFilterGain.addEventListener('input', () => {
    setMicGain(parseFloat(setupFilterGain.value))
  })

  const onFilterChange = () => {
    cameraFilters = {
      brightness: parseFloat(setupFilterBrightness.value),
      contrast: parseFloat(setupFilterContrast.value),
      saturation: parseFloat(setupFilterSaturation.value),
      blur: parseFloat(setupFilterBlur.value),
    }
    if (cameraPipeline) cameraPipeline.setFilters(cameraFilters)
    else applyCssFiltersToLocalPreview()
    saveFilters(cameraFilters)
    syncSetupFilterInputs()
  }
  setupFilterBrightness.addEventListener('input', onFilterChange)
  setupFilterContrast.addEventListener('input', onFilterChange)
  setupFilterSaturation.addEventListener('input', onFilterChange)
  setupFilterBlur.addEventListener('input', onFilterChange)

  setupBtnReset.addEventListener('click', () => {
    cameraFilters = { ...DEFAULT_FILTERS }
    if (cameraPipeline) cameraPipeline.setFilters(cameraFilters)
    else applyCssFiltersToLocalPreview()
    saveFilters(cameraFilters)
    syncSetupFilterInputs()
  })

  btnSetupCancel.addEventListener('click', () => {
    cancelSetup()
  })

  btnSetupJoin.addEventListener('click', async () => {
    await confirmSetup()
  })
}

function cancelSetup() {
  stopMicMeter()
  gainPipeline?.stop()
  gainPipeline = null
  lastMicSourceTrack = null
  cameraPipeline?.stop()
  cameraPipeline = null
  manager?.leave()
  localStream = null
  mediaReady = false
  setupVideo.srcObject = null
  pendingSetup = null
  window.history.replaceState(null, '', window.location.pathname)
  showView('lobby')
}

async function confirmSetup() {
  if (!pendingSetup) return
  const intent = pendingSetup
  const name = setupName.value.trim()
  if (name) {
    localStorage.setItem('peercall-name', name)
    manager.rename(name)
  }

  btnSetupJoin.disabled = true
  showView('connecting')
  connectingDetail.textContent = intent.mode === 'create' ? 'Creando sala...' : 'Uniéndose...'

  try {
    if (intent.mode === 'create') {
      const code = await manager.createRoom()
      window.history.replaceState(null, '', `#${code}`)
      showCallView(code)
    } else {
      await manager.joinRoom(intent.roomCode)
      window.history.replaceState(null, '', `#${intent.roomCode}`)
      showCallView(intent.roomCode)
    }
    pendingSetup = null
    // Don't stop the mic meter — it keeps driving the mic button's level bar
    // during the call. It'll be torn down by manager.leave() when the user
    // hangs up or closes the tab.
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error'
    alert((intent.mode === 'create' ? 'No se pudo crear la sala: ' : 'No se pudo unir: ') + message)
    console.error('[PeerCall] confirmSetup failed:', err)
    showView('setup')
  } finally {
    btnSetupJoin.disabled = false
  }
}

// ─── Mic level meter (setup screen) ───

let micMeterCtx: AudioContext | null = null
let micMeterRaf = 0

function startMicMeter() {
  stopMicMeter()
  if (!localStream) return
  const audioTrack = localStream.getAudioTracks()[0]
  if (!audioTrack) return

  try {
    micMeterCtx = new AudioContext()
    const source = micMeterCtx.createMediaStreamSource(new MediaStream([audioTrack]))
    const analyser = micMeterCtx.createAnalyser()
    analyser.fftSize = 256
    source.connect(analyser)
    const data = new Uint8Array(analyser.frequencyBinCount)

    const tick = () => {
      analyser.getByteTimeDomainData(data)
      let peak = 0
      for (let i = 0; i < data.length; i++) {
        const v = Math.abs(data[i] - 128)
        if (v > peak) peak = v
      }
      const level = Math.min(1, (peak / 128) * 2)
      setupMicLevel.style.width = `${level * 100}%`
      btnMic.style.setProperty('--mic-level', String(level))
      micMeterRaf = requestAnimationFrame(tick)
    }
    tick()
  } catch (err) {
    console.error('[PeerCall] mic meter failed:', err)
  }
}

function stopMicMeter() {
  cancelAnimationFrame(micMeterRaf)
  micMeterRaf = 0
  micMeterCtx?.close().catch(() => {})
  micMeterCtx = null
  setupMicLevel.style.width = '0%'
}

// ─── Device preferences (persisted in localStorage) ───

const DEVICE_PREFS_KEY = 'peercall-devices'

function getDevicePrefs(): { mic?: string; speaker?: string; camera?: string } {
  try {
    return JSON.parse(localStorage.getItem(DEVICE_PREFS_KEY) || '{}')
  } catch {
    return {}
  }
}

function saveDevicePref(key: 'mic' | 'speaker' | 'camera', deviceId: string) {
  const prefs = getDevicePrefs()
  prefs[key] = deviceId
  localStorage.setItem(DEVICE_PREFS_KEY, JSON.stringify(prefs))
}

// ─── Mic acquisition helpers ───

/**
 * Acquire a microphone track. When RNNoise will handle noise suppression,
 * pass `useBrowserNS=false` so the browser's built-in NS and AGC are off —
 * double-processing degrades quality (AGC pumping, spectrum already altered).
 * Echo cancellation stays on either way; RNNoise doesn't replace AEC.
 */
async function getMicTrack(deviceId: string | undefined, useBrowserNS: boolean): Promise<MediaStreamTrack> {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      echoCancellation: { ideal: true },
      noiseSuppression: { ideal: useBrowserNS },
      autoGainControl: { ideal: useBrowserNS },
    },
    video: false,
  })
  return stream.getAudioTracks()[0]
}

function currentMicDeviceId(): string | undefined {
  const track = localStream?.getAudioTracks()[0]
  return track?.getSettings().deviceId || getDevicePrefs().mic
}

/**
 * Replace the audio track in localStream and on every outgoing sender.
 * By default the previous track is stopped. Pass `stopOld=false` when the
 * caller knows the old track is still being consumed by something (e.g.
 * it's the input of a gain pipeline that's about to output this newTrack).
 */
async function replaceLocalAudioTrack(newTrack: MediaStreamTrack, stopOld = true) {
  if (!localStream) return
  const oldTrack = localStream.getAudioTracks()[0]
  if (oldTrack === newTrack) return
  if (oldTrack) {
    if (stopOld) oldTrack.stop()
    localStream.removeTrack(oldTrack)
  }
  localStream.addTrack(newTrack)
  for (const remotePeer of manager.peerList) {
    const pc = getPeerConnection(remotePeer)
    if (pc) {
      const sender = pc.getSenders().find((s) => s.track?.kind === 'audio')
      if (sender) await sender.replaceTrack(newTrack)
    }
  }
}

/**
 * Replace the video track in localStream and on every outgoing sender.
 * The previous track is removed but NOT stopped — the caller (or the
 * pipeline) owns it. Use for pipeline swaps where we don't want to kill
 * the raw camera that the pipeline is still rendering from.
 */
async function replaceLocalVideoTrack(newTrack: MediaStreamTrack, stopOld: boolean) {
  if (!localStream) return
  const oldTrack = localStream.getVideoTracks()[0]
  if (oldTrack) {
    if (stopOld) oldTrack.stop()
    localStream.removeTrack(oldTrack)
  }
  localStream.addTrack(newTrack)
  for (const remotePeer of manager.peerList) {
    const pc = getPeerConnection(remotePeer)
    if (pc) {
      const sender = pc.getSenders().find((s) => s.track?.kind === 'video')
      if (sender) await sender.replaceTrack(newTrack)
    }
  }
}

/**
 * Wrap the current raw camera track in localStream with a filter pipeline
 * (on browsers that support canvas filter) or just apply filters as CSS on
 * the local preview. Safe to call after `startMedia` populated localStream
 * with a fresh camera track.
 */
async function applyCameraPipeline() {
  if (!localStream) return
  const rawTrack = localStream.getVideoTracks()[0]
  if (!rawTrack) return

  if (pipelineSupported) {
    // If an old pipeline exists (e.g. camera device change), tear it down
    // first. The pipeline owns its input track and will stop it.
    cameraPipeline?.stop()
    cameraPipeline = startCameraPipeline(rawTrack, cameraFilters)
    await replaceLocalVideoTrack(cameraPipeline.output, false)
  } else {
    // Raw track stays in localStream. Filters apply as CSS only locally.
    applyCssFiltersToLocalPreview()
  }
  refreshLocalPreview()
}

function applyCssFiltersToLocalPreview() {
  const css = filtersToCss(cameraFilters)
  const localTile = videoTiles.get('local')
  if (localTile) localTile.video.style.filter = css
  setupVideo.style.filter = css
}

/** Apply the current mirror setting to every local preview surface. */
function applyMirrorSetting() {
  setupVideo.classList.toggle('mirror', cameraMirror)
  const localTile = videoTiles.get('local')
  if (localTile) localTile.video.classList.toggle('mirror', cameraMirror)
  setupToggleMirror.checked = cameraMirror
  toggleMirror.checked = cameraMirror
}

function setCameraMirror(enabled: boolean) {
  cameraMirror = enabled
  localStorage.setItem('peercall-mirror', String(enabled))
  applyMirrorSetting()
}

/**
 * Reassign the local tile's video srcObject to pick up a new track cleanly.
 * MediaStream track changes propagate automatically to HTMLMediaElement, but
 * in practice detached-video → canvas → captureStream setups sometimes need
 * a kick to render the new track.
 */
function refreshLocalPreview() {
  const localTile = videoTiles.get('local')
  if (localTile && localStream) {
    localTile.video.srcObject = localStream
  }
  if (localStream) setupVideo.srcObject = localStream
}

/**
 * The deviceId of the camera currently in use. Prefer the pipeline's raw
 * input track (the canvas track has no meaningful deviceId), fall back to
 * whatever is in localStream, then to saved prefs.
 */
function currentCameraDeviceId(): string | undefined {
  const raw = cameraPipeline?.input ?? localStream?.getVideoTracks()[0]
  return raw?.getSettings().deviceId || getDevicePrefs().camera
}

// ─── Shared device/filter actions (used by setup view + call-view panels) ───

async function switchMic(deviceId: string) {
  saveDevicePref('mic', deviceId)
  if (!localStream) return
  try {
    const newTrack = await getMicTrack(deviceId, !noiseSuppression)
    let sourceTrack: MediaStreamTrack = newTrack
    if (noiseSuppression) {
      const newStream = new MediaStream([newTrack])
      const processedStream = await updateNoiseSuppressionSource(newStream)
      if (processedStream) {
        sourceTrack = processedStream.getAudioTracks()[0]
      } else {
        newTrack.stop()
        sourceTrack = await getMicTrack(deviceId, true)
        noiseSuppression = false
        updateNoiseSuppressionUI()
      }
    }
    await publishMic(sourceTrack)
  } catch (err) {
    console.error('[PeerCall] switchMic failed:', err)
  }
}

/**
 * Publish a "source" mic track (raw or NS-processed) to localStream and
 * senders, wrapping it with the gain stage if gain ≠ 1.
 */
async function publishMic(sourceTrack: MediaStreamTrack) {
  lastMicSourceTrack = sourceTrack

  // Rebuild gain pipeline for the new source
  gainPipeline?.stop()
  gainPipeline = null

  let finalTrack: MediaStreamTrack = sourceTrack
  if (micGain !== 1) {
    gainPipeline = startGainPipeline(sourceTrack, micGain)
    finalTrack = gainPipeline.output
  }

  // If the old track in localStream IS the sourceTrack (initial publish after
  // getUserMedia), don't stop it — the gain pipeline is consuming it.
  const currentTrack = localStream?.getAudioTracks()[0]
  const stopOld = currentTrack !== sourceTrack
  await replaceLocalAudioTrack(finalTrack, stopOld)
  if (micMeterCtx) startMicMeter()
}

function setMicGain(value: number) {
  micGain = value
  saveGain(value)
  valGain.textContent = `${Math.round(value * 100)}%`
  setupValGain.textContent = `${Math.round(value * 100)}%`
  if (filterGain.value !== String(value)) filterGain.value = String(value)
  if (setupFilterGain.value !== String(value)) setupFilterGain.value = String(value)

  if (!lastMicSourceTrack) return

  if (value === 1) {
    // Bypass the gain stage entirely
    if (gainPipeline) {
      gainPipeline.stop()
      gainPipeline = null
      replaceLocalAudioTrack(lastMicSourceTrack, false).then(() => {
        if (micMeterCtx) startMicMeter()
      })
    }
  } else if (gainPipeline) {
    // Just tweak the existing GainNode — no click, no track swap
    gainPipeline.setGain(value)
  } else {
    // Build a new gain pipeline on top of the current source;
    // don't stop lastMicSourceTrack — it's the gain pipeline's input now.
    gainPipeline = startGainPipeline(lastMicSourceTrack, value)
    replaceLocalAudioTrack(gainPipeline.output, false).then(() => {
      if (micMeterCtx) startMicMeter()
    })
  }
}

async function switchCamera(deviceId: string) {
  saveDevicePref('camera', deviceId)
  if (!localStream) return
  try {
    const newStream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        deviceId: { exact: deviceId },
      },
    })
    const rawTrack = newStream.getVideoTracks()[0]
    if (!rawTrack) return

    if (pipelineSupported) {
      cameraPipeline?.stop()
      cameraPipeline = startCameraPipeline(rawTrack, cameraFilters)
      await replaceLocalVideoTrack(cameraPipeline.output, false)
    } else {
      await replaceLocalVideoTrack(rawTrack, true)
      applyCssFiltersToLocalPreview()
    }
    refreshLocalPreview()
  } catch (err) {
    console.error('[PeerCall] switchCamera failed:', err)
  }
}

async function switchSpeaker(deviceId: string) {
  saveDevicePref('speaker', deviceId)
  const targets: HTMLVideoElement[] = [setupVideo, ...Array.from(videoTiles.values()).map((t) => t.video)]
  for (const el of targets) {
    try {
      await (el as unknown as { setSinkId?: (id: string) => Promise<void> }).setSinkId?.(deviceId)
    } catch (err) {
      console.error('[PeerCall] setSinkId failed:', err)
    }
  }
}

async function setNoiseSuppressionEnabled(enabled: boolean) {
  if (!localStream) return
  if (enabled === noiseSuppression) return
  const deviceId = currentMicDeviceId()

  if (enabled) {
    try {
      const cleanTrack = await getMicTrack(deviceId, false)
      const cleanStream = new MediaStream([cleanTrack])
      const processedStream = await enableNoiseSuppression(cleanStream)
      const processedTrack = processedStream.getAudioTracks()[0]
      if (processedTrack) await publishMic(processedTrack)
      noiseSuppression = true
    } catch (err) {
      console.error('[PeerCall] NS enable failed:', err)
      disableNoiseSuppression()?.stop()
      noiseSuppression = false
    }
  } else {
    try {
      disableNoiseSuppression()?.stop()
      const restoredTrack = await getMicTrack(deviceId, true)
      await publishMic(restoredTrack)
    } catch (err) {
      console.error('[PeerCall] NS disable failed:', err)
    }
    noiseSuppression = false
  }
  updateNoiseSuppressionUI()
}

function updateNoiseSuppressionUI() {
  btnNoise.classList.toggle('active', noiseSuppression)
  replaceIcon(btnNoise, noiseSuppression ? 'volume-2' : 'volume-x')
  setupToggleNoise.checked = noiseSuppression
}

// ─── Media request ───

function addNoMediaTile() {
  const tile = createVideoTile('local', manager.myName, true)
  const placeholder = document.createElement('div')
  placeholder.className = 'no-camera-placeholder'
  placeholder.textContent = '📷'
  placeholder.style.cssText =
    'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:3rem;background:#1a1a2e;z-index:1;'
  tile.container.style.position = 'relative'
  tile.container.insertBefore(placeholder, tile.video)
  videoGrid.appendChild(tile.container)
  updateGridCount()
}

// ─── Manager events ───

function setupManagerEvents(mgr: PeerCallManager) {
  mgr.on((event) => {
    switch (event.type) {
      case 'peer-joined':
        updatePeerName(event.peer.id, event.peer.name)
        break
      case 'peer-left':
        removeVideoTile(event.peerId)
        break
      case 'stream':
        addRemoteVideo(event.peerId, event.stream)
        break
      case 'stream-removed':
        removeVideoTile(event.peerId)
        break
      case 'chat':
        addChatMessage(event.peerId, event.text, false)
        break
      case 'audio-toggle':
        updateMuteIndicator(event.peerId, !event.enabled)
        break
      case 'rename':
        updatePeerName(event.peerId, event.name)
        break
      case 'video-toggle':
      case 'screen-stop':
      case 'error':
        break
    }
  })
}

// ─── UI Helpers ───

function showView(name: 'lobby' | 'call' | 'connecting' | 'setup') {
  lobby.classList.toggle('hidden', name !== 'lobby')
  callView.classList.toggle('hidden', name !== 'call')
  connecting.classList.toggle('hidden', name !== 'connecting')
  setupView.classList.toggle('hidden', name !== 'setup')
}

function showCallView(code: string) {
  roomCode.textContent = code
  showView('call')
  if (localStream && localStream.getVideoTracks().length > 0) {
    addLocalVideo(localStream)
  } else {
    addNoMediaTile()
  }
  // Sync the call-view control states with whatever was configured in setup
  updateMicCameraButtons()
  btnCamera.classList.toggle('off', !cameraOn)
  btnMic.classList.toggle('off', !micOn)
  updateNoiseSuppressionUI()
  applyMirrorSetting()
  setupCallControls()
}

function updateMicCameraButtons() {
  replaceIcon(btnMic, micOn ? 'mic' : 'mic-off')
  replaceIcon(btnCamera, cameraOn ? 'video' : 'video-off')
}

// ─── Rename helper ───

function applyRename(newName: string) {
  if (!newName.trim()) return
  manager.rename(newName.trim())
  localStorage.setItem('peercall-name', newName.trim())
  updateLocalNameTag()
  inputDisplayName.value = manager.myName
}

function updateLocalNameTag() {
  const localTile = videoTiles.get('local')
  if (localTile) {
    localTile.nameTag.textContent = `${manager.myName} (you)`
  }
}

function setupCallControls() {
  btnMic.addEventListener('click', async () => {
    if (!mediaReady && !micOn) {
      try {
        localStream = await manager.startMedia(cameraOn, true)
        addLocalVideo(localStream)
        mediaReady = true
        micOn = true
      } catch (err) {
        console.error('[PeerCall] Could not enable mic:', err)
        return
      }
    } else {
      micOn = !micOn
    }
    manager.toggleAudio(micOn)
    btnMic.classList.toggle('off', !micOn)
    replaceIcon(btnMic, micOn ? 'mic' : 'mic-off')
  })

  btnCamera.addEventListener('click', async () => {
    if (!mediaReady && !cameraOn) {
      try {
        localStream = await manager.startMedia(true, micOn)
        addLocalVideo(localStream)
        mediaReady = true
        cameraOn = true
        await applyCameraPipeline()
      } catch (err) {
        console.error('[PeerCall] Could not enable camera:', err)
        return
      }
    } else {
      cameraOn = !cameraOn
      manager.toggleVideo(cameraOn)
    }
    btnCamera.classList.toggle('off', !cameraOn)
    replaceIcon(btnCamera, cameraOn ? 'video' : 'video-off')
  })

  btnScreen.addEventListener('click', async () => {
    if (!screenSharing) {
      const stream = await manager.startScreenShare()
      if (stream) {
        screenSharing = true
        btnScreen.classList.add('active')
      }
    } else {
      manager.stopScreenShare()
      screenSharing = false
      btnScreen.classList.remove('active')
    }
  })

  // ─── Noise suppression ───

  btnNoise.addEventListener('click', async () => {
    await setNoiseSuppressionEnabled(!noiseSuppression)
  })

  btnChat.addEventListener('click', () => {
    chatOpen = !chatOpen
    chatPanel.classList.toggle('hidden', !chatOpen)
    if (chatOpen) chatInput.focus()
  })

  btnCloseChat.addEventListener('click', () => {
    chatOpen = false
  })

  btnLeave.addEventListener('click', () => {
    manager.leave()
    window.location.hash = ''
    window.location.reload()
  })

  btnCopy.addEventListener('click', () => {
    const url = `${window.location.origin}${window.location.pathname}#${manager.roomCode}`
    navigator.clipboard.writeText(url).then(() => {
      replaceIcon(btnCopy, 'check')
      setTimeout(() => replaceIcon(btnCopy, 'copy'), 2000)
    })
  })

  formChat.addEventListener('submit', (e) => {
    e.preventDefault()
    const text = chatInput.value.trim()
    if (!text) return
    manager.sendChat(text)
    addChatMessage('self', text, true)
    chatInput.value = ''
  })

  // ─── Settings panel (device selection + rename) ───

  let settingsOpen = false

  btnSettings.addEventListener('click', async () => {
    settingsOpen = !settingsOpen
    settingsPanel.classList.toggle('hidden', !settingsOpen)
    if (settingsOpen) {
      await populateDeviceSelectors()
      inputDisplayName.value = manager.myName
    }
  })

  btnCloseSettings.addEventListener('click', () => {
    settingsOpen = false
    settingsPanel.classList.add('hidden')
  })

  // Rename from settings input
  inputDisplayName.addEventListener('change', () => {
    applyRename(inputDisplayName.value)
  })
  inputDisplayName.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      inputDisplayName.blur()
    }
  })

  // ─── Camera panel (device + filters) ───

  let cameraPanelOpen = false

  function updateFilterLabels() {
    valBrightness.textContent = `${Math.round(cameraFilters.brightness * 100)}%`
    valContrast.textContent = `${Math.round(cameraFilters.contrast * 100)}%`
    valSaturation.textContent = `${Math.round(cameraFilters.saturation * 100)}%`
    valBlur.textContent = `${cameraFilters.blur}px`
  }

  function syncFilterInputs() {
    filterBrightness.value = String(cameraFilters.brightness)
    filterContrast.value = String(cameraFilters.contrast)
    filterSaturation.value = String(cameraFilters.saturation)
    filterBlur.value = String(cameraFilters.blur)
    updateFilterLabels()
  }

  syncFilterInputs()

  btnCameraMenu.addEventListener('click', async () => {
    cameraPanelOpen = !cameraPanelOpen
    cameraPanel.classList.toggle('hidden', !cameraPanelOpen)
    if (cameraPanelOpen) {
      await populateDeviceSelectors()
      syncFilterInputs()
    }
  })

  btnCloseCamera.addEventListener('click', () => {
    cameraPanelOpen = false
    cameraPanel.classList.add('hidden')
  })

  function applyFilterChange() {
    cameraFilters = {
      brightness: parseFloat(filterBrightness.value),
      contrast: parseFloat(filterContrast.value),
      saturation: parseFloat(filterSaturation.value),
      blur: parseFloat(filterBlur.value),
    }
    if (cameraPipeline) {
      cameraPipeline.setFilters(cameraFilters)
    } else {
      applyCssFiltersToLocalPreview()
    }
    updateFilterLabels()
    saveFilters(cameraFilters)
  }

  filterBrightness.addEventListener('input', applyFilterChange)
  filterContrast.addEventListener('input', applyFilterChange)
  filterSaturation.addEventListener('input', applyFilterChange)
  filterBlur.addEventListener('input', applyFilterChange)

  btnResetFilters.addEventListener('click', () => {
    cameraFilters = { ...DEFAULT_FILTERS }
    if (cameraPipeline) {
      cameraPipeline.setFilters(cameraFilters)
    } else {
      applyCssFiltersToLocalPreview()
    }
    saveFilters(cameraFilters)
    syncFilterInputs()
  })

  toggleMirror.addEventListener('change', () => {
    setCameraMirror(toggleMirror.checked)
  })

  filterGain.value = String(micGain)
  valGain.textContent = `${Math.round(micGain * 100)}%`
  filterGain.addEventListener('input', () => {
    setMicGain(parseFloat(filterGain.value))
  })

  selectAudioInput.addEventListener('change', async () => {
    const deviceId = selectAudioInput.value
    if (deviceId) await switchMic(deviceId)
  })

  if (sinkSupported) {
    selectAudioOutput.addEventListener('change', async () => {
      const deviceId = selectAudioOutput.value
      if (deviceId) await switchSpeaker(deviceId)
    })
  }

  selectVideoInput.addEventListener('change', async () => {
    const deviceId = selectVideoInput.value
    if (deviceId) await switchCamera(deviceId)
  })
}

// ─── Settings Panel ───

async function populateDeviceSelectors() {
  // On iOS Safari, device labels are empty until getUserMedia has been
  // granted.  Re-enumerate after permission is already active.
  // Also, iOS does not support audiooutput / setSinkId — we hide the
  // speaker selector in that case.

  try {
    const devices = await navigator.mediaDevices.enumerateDevices()

    // Audio inputs (microphones)
    const audioInputs = devices.filter((d) => d.kind === 'audioinput')
    selectAudioInput.innerHTML = ''
    if (audioInputs.length === 0) {
      const opt = document.createElement('option')
      opt.value = ''
      opt.textContent = 'No microphones found'
      opt.disabled = true
      selectAudioInput.appendChild(opt)
    } else {
      audioInputs.forEach((d, i) => {
        const opt = document.createElement('option')
        opt.value = d.deviceId
        // Label may be empty on iOS until permission is granted;
        // fall back to generic name
        opt.textContent = d.label || `Microphone ${i + 1}`
        selectAudioInput.appendChild(opt)
      })
    }

    // Audio outputs (speakers) — not supported on iOS/Safari
    const audioOutputs = devices.filter((d) => d.kind === 'audiooutput')
    const outputLabel = selectAudioOutput.closest('label')
    if (!sinkSupported || audioOutputs.length === 0) {
      // Hide speaker selector entirely on unsupported browsers
      if (outputLabel) outputLabel.classList.add('hidden')
    } else {
      if (outputLabel) outputLabel.classList.remove('hidden')
      selectAudioOutput.innerHTML = ''
      audioOutputs.forEach((d, i) => {
        const opt = document.createElement('option')
        opt.value = d.deviceId
        opt.textContent = d.label || `Speaker ${i + 1}`
        selectAudioOutput.appendChild(opt)
      })
    }

    // Video inputs (cameras)
    const videoInputs = devices.filter((d) => d.kind === 'videoinput')
    selectVideoInput.innerHTML = ''
    if (videoInputs.length === 0) {
      const opt = document.createElement('option')
      opt.value = ''
      opt.textContent = 'No cameras found'
      opt.disabled = true
      selectVideoInput.appendChild(opt)
    } else {
      videoInputs.forEach((d, i) => {
        const opt = document.createElement('option')
        opt.value = d.deviceId
        opt.textContent = d.label || `Camera ${i + 1}`
        selectVideoInput.appendChild(opt)
      })
    }

    // Select saved device preferences, fall back to current active device
    const saved = getDevicePrefs()
    if (saved.mic && audioInputs.some((d) => d.deviceId === saved.mic)) {
      selectAudioInput.value = saved.mic
    } else if (localStream) {
      const currentAudio = localStream.getAudioTracks()[0]
      if (currentAudio) {
        const settings = currentAudio.getSettings()
        if (settings.deviceId) selectAudioInput.value = settings.deviceId
      }
    }

    if (saved.speaker && audioOutputs.some((d) => d.deviceId === saved.speaker)) {
      selectAudioOutput.value = saved.speaker
    }

    if (saved.camera && videoInputs.some((d) => d.deviceId === saved.camera)) {
      selectVideoInput.value = saved.camera
    } else {
      const activeId = currentCameraDeviceId()
      if (activeId && videoInputs.some((d) => d.deviceId === activeId)) {
        selectVideoInput.value = activeId
      }
    }
  } catch (err) {
    console.error('[PeerCall] enumerateDevices failed:', err)
  }
}

// ─── Video Tiles ───

function addLocalVideo(stream: MediaStream) {
  removeVideoTile('local')
  const tile = createVideoTile('local', manager.myName, true)
  tile.video.srcObject = stream
  tile.video.autoplay = true
  tile.video.muted = true
  tile.video.playsInline = true
  tile.video.classList.toggle('mirror', cameraMirror)
  // Make local name tag clickable for inline rename
  tile.nameTag.classList.add('editable')
  tile.nameTag.title = 'Click to change your name'
  tile.nameTag.addEventListener('click', () => startInlineRename(tile.nameTag))
  videoGrid.appendChild(tile.container)
  updateGridCount()
}

function addRemoteVideo(peerId: string, stream: MediaStream) {
  removeVideoTile(peerId)
  const peer = manager.peerList.find((p) => p.id === peerId)
  const name = peer?.name || '...'
  const tile = createVideoTile(peerId, name, false)
  tile.video.srcObject = stream
  tile.video.autoplay = true
  tile.video.playsInline = true
  videoGrid.appendChild(tile.container)
  updateGridCount()
}

function createVideoTile(id: string, name: string, isLocal: boolean) {
  const container = document.createElement('div')
  container.className = 'video-tile'
  container.dataset.peerId = id

  const video = document.createElement('video')
  video.autoplay = true
  video.playsInline = true
  if (isLocal) video.muted = true

  const nameTag = document.createElement('span')
  nameTag.className = 'name-tag'
  nameTag.textContent = isLocal ? `${name} (you)` : name

  const mutedInd = document.createElement('div')
  mutedInd.className = 'muted-indicator hidden'
  mutedInd.textContent = '🔇'

  container.appendChild(video)
  container.appendChild(nameTag)
  container.appendChild(mutedInd)

  const tile = { container, video, nameTag, mutedInd }
  videoTiles.set(id, tile)
  return tile
}

function removeVideoTile(peerId: string) {
  const tile = videoTiles.get(peerId)
  if (tile) {
    tile.container.remove()
    videoTiles.delete(peerId)
    updateGridCount()
  }
}

function updatePeerName(peerId: string, name: string) {
  const tile = videoTiles.get(peerId)
  if (tile) {
    tile.nameTag.textContent = peerId === 'local' ? `${name} (you)` : name
  }
}

function updateMuteIndicator(peerId: string, muted: boolean) {
  const tile = videoTiles.get(peerId)
  if (tile) tile.mutedInd.classList.toggle('hidden', !muted)
}

function updateGridCount() {
  videoGrid.dataset.count = String(videoTiles.size)
}

// ─── Inline rename (click on name tag) ───

function startInlineRename(nameTag: HTMLSpanElement) {
  const currentName = manager.myName
  const input = document.createElement('input')
  input.type = 'text'
  input.className = 'name-tag-input'
  input.value = currentName
  input.maxLength = 24
  input.style.width = `${Math.max(60, currentName.length * 9)}px`

  nameTag.style.display = 'none'
  nameTag.parentElement!.appendChild(input)
  input.focus()
  input.select()

  const finishRename = () => {
    const newName = input.value.trim()
    input.remove()
    nameTag.style.display = ''
    if (newName && newName !== currentName) {
      applyRename(newName)
    } else {
      updateLocalNameTag()
    }
  }

  input.addEventListener('blur', finishRename)
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      input.blur()
    } else if (e.key === 'Escape') {
      input.value = currentName
      input.blur()
    }
  })
}

// ─── Chat ───

function addChatMessage(from: string, text: string, isSelf: boolean) {
  const msg = document.createElement('div')
  msg.className = `chat-msg ${isSelf ? 'self' : ''}`

  if (!isSelf) {
    const peer = manager.peerList.find((p) => p.id === from)
    const nameDiv = document.createElement('div')
    nameDiv.className = 'chat-msg-name'
    nameDiv.textContent = peer?.name || from.slice(0, 8)
    msg.appendChild(nameDiv)
  }

  const textDiv = document.createElement('div')
  textDiv.className = 'chat-msg-text'
  textDiv.textContent = text
  msg.appendChild(textDiv)

  chatMessages.appendChild(msg)
  chatMessages.scrollTop = chatMessages.scrollHeight
}

// ─── URL hash change ───

window.addEventListener('hashchange', () => {
  const code = window.location.hash.slice(1).trim()
  if (code && code.length >= 4) joinRoom(code)
})

// ─── Tab close / navigation cleanup ───
// Close connections gracefully so remote peers see us drop out immediately
// instead of waiting for ICE timeout (~15s of frozen video otherwise).
// `pagehide` covers tab close, navigation, and iOS Safari; `beforeunload`
// covers extra desktop cases. Both calling leave() is idempotent.
const cleanupOnUnload = () => {
  try {
    manager?.leave()
  } catch {
    // ignore
  }
}
window.addEventListener('pagehide', cleanupOnUnload)
window.addEventListener('beforeunload', cleanupOnUnload)

// ─── Start ───

init()
